import type { ExternalScenarioDetailDto } from '../../../features/external-tests/useExternalScenarioDetail';
import type { RunBarSegment } from '../../components/RunResultBars';

const verdictLabel = {
  passed: 'Passed',
  failed: 'Failed',
  flaky: 'Flaky',
  skipped: 'Skipped',
} as const;

type HistoryRun = ExternalScenarioDetailDto['history']['runs'][number];

/**
 * Both the item-detail history strip and the catalog's per-row strip need
 * this math, but the catalog's recent-run rows don't carry an `itemId` or
 * `createdAt` — only `runStatus`/`verdict`. Typing against a `Pick` keeps
 * either shape assignable instead of coupling to the full `HistoryRun` DTO.
 */
export type RunResultInput = Pick<HistoryRun, 'runStatus' | 'verdict'>;

export function displayLabel(run: RunResultInput): string {
  if (run.runStatus === 'open' || run.runStatus === 'collecting') return 'Running';
  if (run.runStatus === 'canceled') return 'Canceled';
  return verdictLabel[run.verdict];
}

export function externalHistoryRunResult(run: RunResultInput): RunBarSegment['result'] {
  if (run.runStatus === 'open' || run.runStatus === 'collecting') return 'running';
  if (run.runStatus === 'canceled') return 'skip';
  if (run.verdict === 'passed') return 'pass';
  if (run.verdict === 'failed') return 'fail';
  if (run.verdict === 'flaky') return 'flaky';
  return 'skip';
}

/**
 * Marker + tooltip label for one history bar. Only failed, finished runs whose
 * diagnosis was inherited from an earlier run are marked.
 */
export function externalHistoryBarLabel(
  run: Pick<HistoryRun, 'runStatus' | 'verdict' | 'repeatedDiagnosis'>,
  isActive: boolean
): { marked: boolean; tooltip: string; ariaLabel: string } {
  const marked = run.repeatedDiagnosis && externalHistoryRunResult(run) === 'fail';
  const label = marked ? 'Failed · same failure as an earlier run' : displayLabel(run);
  return {
    marked,
    tooltip: `${isActive ? 'Current run · ' : ''}${label}`,
    ariaLabel: `Open ${marked ? 'Failed run · same failure as an earlier run' : `${label} run`}${isActive ? ' (current)' : ''}`,
  };
}

export function externalHistoryPassRate(history: RunResultInput[]): number | null {
  const terminal = history.filter((run) => run.runStatus === 'sealed' && run.verdict !== 'skipped');
  const passCount = terminal.filter(
    (run) => run.verdict === 'passed' || run.verdict === 'flaky'
  ).length;
  return terminal.length > 0 ? Math.round((passCount / terminal.length) * 100) : null;
}
